import { useRecoilState, useSetRecoilState } from 'recoil';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import axios, { endPoints } from 'api/request';
import { draftTeams as recoilStoreDraftTeams, sspSubmitOrder, validOnboardingForm } from 'pages/provisioning/day2/github-onboarding-new/store';
import {
    sanitizeBusinessApplicationName,
    sanitizeCapabilityName,
    getGithubOrgUnit,
    getTeamMemberLimit,
} from 'pages/provisioning/day2/github-onboarding-new/utils';
import {
    TEAM_SUFFIX_DEVELOPERS,
    TEAM_SUFFIX_DEVOPS,
    MAX_DEVOPS_MEMBER_REACHED_MSG,
    MAX_MEMBER_REACHED_MSG,
} from 'pages/provisioning/day2/github-onboarding-new/utils/constant';
import logger from 'utils/logger';

const useTeamActions = () => {
    const [teams, setTeams] = useRecoilState(recoilStoreDraftTeams);
    const [order = {}] = useRecoilState(sspSubmitOrder);
    const setValidForm = useSetRecoilState(validOnboardingForm);

    const { capability, capabilityDetails, businessApplication } = order;

    const getTeamLimitMessage = (index) => {
        const team = teams[index];
        if (!team) return null;
        const teamName = team.name || '';
        const existingOrgMembers = team.existingOrgMembers || [];
        const members = team.members || [];
        const teamLimit = getTeamMemberLimit(teamName, existingOrgMembers);
        const totalMemberCount = members.length + existingOrgMembers.length;
        if (teamLimit !== null && totalMemberCount >= teamLimit) {
            return teamName.toLowerCase().endsWith(TEAM_SUFFIX_DEVOPS) ? MAX_DEVOPS_MEMBER_REACHED_MSG : MAX_MEMBER_REACHED_MSG;
        }
        return null;
    };

    const validateMemberMutation = useMutation(
        async (gpid) => {
            const response = await axios.get(`${endPoints.day2Operation?.ProjectOnboarding?.Jwt?.validateMember}${gpid}`);
            return response?.data;
        },
        {
            onError: (error) => {
                logger.error('Validation failed:', error);
            },
        }
    );

    const copilotLicenseMutation = useMutation(
        async (email) => {
            const response = await axios.get(endPoints.day2Operation.GithubOnboarding.githubCopilotLicense(email));
            return response;
        },
        {
            onError: (error) => {
                logger.warn('Copilot license check failed:', error);
            },
        }
    );

    // eslint-disable-next-line no-unused-vars
    const addTeamMember = async (index, newMember, options = {}) => {
        const gpid = newMember?.login?.split('_')?.[0];

        if (!options?.skipGitHubAccountValidation) {
            // Validate if user has GitHub account
            try {
                const organizationUnit = getGithubOrgUnit(order?.environment);
                const response = await axios.get(
                    endPoints.day2Operation?.GithubOnboarding?.validateGitAccount({
                        organizationUnit,
                        gpid,
                    })
                );
                const data = response?.data;

                if (data?.hasGitHubAccount === true) {
                    setValidForm((prev) => ({
                        ...prev,
                        isGitHubAccountMissing: false,
                    }));
                } else if (data?.hasGitHubAccount === false) {
                    setValidForm((prev) => ({
                        ...prev,
                        isGitHubAccountMissing: true,
                    }));
                    return {
                        isValidMember: false,
                    };
                }
            } catch (error) {
                logger.warn('GitHub account validation error:', error);
                return {
                    isValidMember: false,
                };
            }
        }

        // TODO: TEMPORARILY DISABLED - DevOps training validation
        // const teamName = teams[index]?.name?.toLowerCase() || '';
        // const requiresValidation = teamName.endsWith('devops');

        // validate member completed devops training only if team is devops team and add only if valid
        /*
        if (requiresValidation) {
            try {
                const result = await validateMemberMutation.mutateAsync(gpid);
                if (result?.status !== 'Completed') {
                    setValidForm((prev) => ({
                        ...prev,
                        isGithubADGroupTrainingValid: true,
                        isDuplicateGithubADGroupMember: false,
                    }));
                    return { isValidMember: false, message: 'Member has not completed devops training.' };
                }
            } catch {
                return { isValidMember: false, message: 'Error occured while Validating.' };
            }
        }
        */

        return { isValidMember: true, message: 'Member validated successfully.' };
    };

    const removeTeamMember = (index, login) => {
        try {
            const team = teams[index];
            const memberToRemove = team?.members?.find((member) => member.login === login);

            if (!memberToRemove) {
                toast.error('Member not found in team');
                return;
            }

            setTeams((prevTeams) =>
                prevTeams.map((currentTeam, i) =>
                    i === index
                        ? {
                              ...currentTeam,
                              members: currentTeam.members.filter((member) => member.login !== login),
                          }
                        : currentTeam
                )
            );

            const memberName = memberToRemove.name || login;
            const teamName = team?.name || 'team';
            toast.success(`Member "${memberName}" has been successfully removed from ${teamName}`);
        } catch (error) {
            logger.error('Error removing team member:', error);
            toast.error('Failed to remove member. Please try again.');
        }
    };

    const removeExistingOrgMember = async (index, githubUser, details) => {
        try {
            const gpId = (githubUser || '').split('_')[0];
            const team = teams[index];

            if (!team) {
                toast.error('Team not found');
                return;
            }

            const memberExists = team.existingOrgMembers?.some((m) => m.githubUser === githubUser);
            if (!memberExists) {
                toast.error('Member not found in existing members');
                return;
            }

            // Check copilot license
            let hasCopilotLicense = false;
            if (details?.email) {
                try {
                    const licenseResponse = await copilotLicenseMutation.mutateAsync(details.email);
                    hasCopilotLicense = licenseResponse?.data?.isValidCopilotLicense === true && licenseResponse?.isSuccess === true;
                } catch (licenseError) {
                    logger.warn('Copilot license check failed:', licenseError);
                    hasCopilotLicense = false;
                }
            }

            setTeams((prevTeams) =>
                prevTeams.map((currentTeam, i) => {
                    if (i !== index) return currentTeam;

                    const currentExistingMembers = (currentTeam.existingOrgMembers || []).filter((m) => m.githubUser !== githubUser);
                    const previousRemovedMembers = Array.isArray(currentTeam.removedMembers) ? currentTeam.removedMembers : [];

                    const alreadyExists = previousRemovedMembers.some((rm) => {
                        if (!rm) return false;
                        const id = typeof rm === 'string' ? rm : rm.gpId || rm.gpid || rm.login;
                        return id === gpId;
                    });

                    const removalEntry = {
                        name: details?.name || '',
                        gpId,
                        emailId: details?.email || '',
                        hasCopilotLicense,
                    };

                    return {
                        ...currentTeam,
                        existingOrgMembers: currentExistingMembers,
                        removedMembers: alreadyExists ? previousRemovedMembers : [...previousRemovedMembers, removalEntry],
                    };
                })
            );

            const memberName = details?.name || gpId;
            const teamName = team?.name || 'team';
            toast.success(`Member "${memberName}" has been successfully removed from ${teamName}`);
        } catch (error) {
            logger.error('Error removing existing org member:', error);
            toast.error('Failed to remove member. Please try again.');
        }
    };

    const addNewTeam = () => {
        const capabilityValue = sanitizeCapabilityName(capabilityDetails?.value || capability);
        const businessApplicationValue = sanitizeBusinessApplicationName(businessApplication ?? '');
        const customNameValue = '';
        const categorizationValue = TEAM_SUFFIX_DEVELOPERS;

        const newTeam = {
            capability: capabilityValue,
            appName: businessApplicationValue,
            customName: customNameValue,
            categorization: categorizationValue,
            name: [capabilityValue, businessApplicationValue, customNameValue, categorizationValue].join('-'),
            members: [],
            originalMembers: [],
            removedMembers: [],
            existingOrgMembers: [],
            repos: [], // Custom teams start with empty repos - users must select repos in Repository Access Control
            originalRepos: [],
            isNew: true,
            isDefault: false,
            visibility: 'public',
            hasExistingMembers: false,
            teamExistsInOrg: false,
        };

        setTeams((prevTeams) => [...prevTeams, newTeam]);
    };

    const deleteTeam = (index) => {
        try {
            const team = teams[index];
            if (!team) {
                toast.error('Team not found');
                return;
            }

            const teamName = team?.name || '';
            setTeams((prevTeams) => prevTeams.filter((_, i) => i !== index));
            toast.success(`Team "${teamName}" has been successfully deleted`);
        } catch (error) {
            logger.error('Error deleting team:', error);
            toast.error('Failed to delete team. Please try again.');
        }
    };

    return {
        addTeamMember,
        removeTeamMember,
        removeExistingOrgMember,
        addNewTeam,
        deleteTeam,
        getTeamLimitMessage,
        isLoading: validateMemberMutation.isLoading,
        isRemovingExistingMember: copilotLicenseMutation?.isLoading,
        isSuccess: validateMemberMutation.isSuccess,
    };
};

export default useTeamActions;
